import DOMPurify from 'dompurify';
import { MEDIA_ALIGN_ATTRIBUTE, parseMediaLayout } from './mediaLayoutStyle';

/**
 * Rend du HTML (ex. contenu d'un éditeur riche TipTap) en le SANITISANT via
 * DOMPurify avant injection — défense en profondeur contre un XSS stocké même
 * lorsque la source est censée être de confiance (admin).
 */
const ALLOWED_TAGS = [
  'p', 'br', 'span', 'div',
  'b', 'strong', 'i', 'em', 'u', 's', 'mark',
  'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4',
  'blockquote', 'code', 'pre', 'hr', 'a',
];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

// Attributs ajoutés par les options médias. Volontairement courts : ni `srcdoc`,
// ni `allow` (qui déléguerait caméra, micro… au cadre embarqué). `style` n'est
// admis qu'avec les images, et réécrit par un hook : il n'en reste que la
// largeur du média (cf. `mediaLayoutStyle.ts`), sur les seules balises médias.
const IMAGE_ATTR = ['src', 'alt', 'title', 'width', 'height', 'style'];

/** Balises dont la mise en page (largeur, alignement) est conservée. Tout autre `style` est retiré. */
const MEDIA_LAYOUT_NODE_NAMES = ['IMG', 'VIDEO'];
// Vidéo : ni `autoplay`, ni `loop`, ni `muted` — une vidéo ne se lance jamais
// seule. `<source>` n'est pas admis : la source est dans `src`, contrôlée ici.
const VIDEO_ATTR = ['src', 'controls', 'width', 'height', 'poster', 'preload', 'playsinline', 'style'];
const IFRAME_ATTR = ['src', 'title', 'width', 'height', 'allowfullscreen'];

/** Seuls hôtes dont une `iframe` peut être rendue — mêmes valeurs que le filtre de l'API (`news-html.util.ts`). */
const YOUTUBE_EMBED_HOSTNAMES = ['www.youtube-nocookie.com', 'www.youtube.com'];

/**
 * Vrai pour une URL d'intégration YouTube et rien d'autre : https, hôte connu,
 * chemin `/embed/…`. Une URL relative ou sans schéma fait échouer `new URL` et
 * est donc refusée.
 */
function isYoutubeEmbedUrl(src: string | null | undefined): boolean {
  if (!src) return false;
  try {
    const url = new URL(src);
    return (
      url.protocol === 'https:'
      && YOUTUBE_EMBED_HOSTNAMES.includes(url.hostname)
      && url.pathname.startsWith('/embed/')
    );
  } catch {
    return false;
  }
}

function isHttpUrl(src: string | null | undefined): boolean {
  if (!src) return false;
  try {
    const { protocol } = new URL(src);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

/**
 * Instance DÉDIÉE aux rendus avec médias. Les hooks DOMPurify sont globaux à une
 * instance : les poser sur l'instance par défaut les appliquerait à tous les
 * `DOMPurify.sanitize` de l'application. Créée au premier usage.
 */
let mediaPurifier: ReturnType<typeof DOMPurify> | null = null;

function getMediaPurifier() {
  if (mediaPurifier) return mediaPurifier;
  const purifier = DOMPurify(window);

  // Le filtrage par balise ne suffit pas : `iframe` autorisée, c'est n'importe
  // quel site embarqué. On retire ici toute iframe qui n'est pas une vidéo
  // YouTube, et toute image qui n'est pas servie en http(s) (pas de `data:`).
  purifier.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName !== 'iframe' && data.tagName !== 'img' && data.tagName !== 'video') return;
    const element = node as Element;
    const src = element.getAttribute?.('src');
    // Vidéo : source en http(s), comme une image. La restriction à NOS fichiers
    // est faite par l'API à l'enregistrement, qui seule connaît leur origine.
    const accepted = data.tagName === 'iframe' ? isYoutubeEmbedUrl(src) : isHttpUrl(src);
    if (!accepted) element.parentNode?.removeChild(element);
  });

  purifier.addHook('afterSanitizeAttributes', (node) => {
    // `style` : DOMPurify ne filtre pas les déclarations CSS. On le RÉÉCRIT donc
    // entièrement — largeur seule sur un média, rien ailleurs — au lieu de le
    // laisser passer : ni `position`, ni `url()`, ni quoi que ce soit d'autre.
    // L'alignement, stocké dans `text-align`, devient un attribut que la feuille
    // de style du conteneur traduit en marges.
    if (node.hasAttribute?.('style')) {
      const layout = MEDIA_LAYOUT_NODE_NAMES.includes(node.nodeName)
        ? parseMediaLayout(node.getAttribute('style'))
        : { width: null, align: null };
      node.removeAttribute('style');
      if (layout.width) node.setAttribute('style', `width: ${layout.width}`);
      if (layout.align) node.setAttribute(MEDIA_ALIGN_ATTRIBUTE, layout.align);
    }
    if (node.nodeName === 'IFRAME') {
      // YouTube refuse la lecture (« erreur 153 ») si le lecteur embarqué ne
      // reçoit pas de référent : on impose la politique plutôt que de dépendre
      // de celle de la page hôte.
      node.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      node.setAttribute('loading', 'lazy');
    }
    if (node.nodeName === 'IMG') {
      node.setAttribute('loading', 'lazy');
    }
    if (node.nodeName === 'VIDEO') {
      // Commandes toujours visibles, lecture jamais automatique, et pas de
      // téléchargement du fichier (jusqu'à 100 Mo) au simple affichage du texte.
      node.setAttribute('controls', '');
      node.setAttribute('playsinline', '');
      node.removeAttribute('autoplay');
      if (node.getAttribute('preload') !== 'none') node.setAttribute('preload', 'metadata');
      if (node.hasAttribute('poster') && !isHttpUrl(node.getAttribute('poster'))) node.removeAttribute('poster');
    }
  });

  mediaPurifier = purifier;
  return purifier;
}

export function SafeHtml({
  html,
  className,
  allowImages = false,
  allowYoutubeEmbeds = false,
  allowVideos = false,
}: {
  html: string | null | undefined;
  className?: string;
  /**
   * Laisse passer les `<img>` dont la source est en http(s), avec leur mise en
   * page (largeur, alignement) et rien d'autre de leur `style`. Désactivé par défaut.
   */
  allowImages?: boolean;
  /**
   * Laisse passer les `<iframe>` de vidéos YouTube (`www.youtube-nocookie.com` et
   * `www.youtube.com`, chemin `/embed/`) — et UNIQUEMENT celles-là : toute autre
   * iframe reste retirée. Désactivé par défaut.
   */
  allowYoutubeEmbeds?: boolean;
  /**
   * Laisse passer les `<video>` dont la source est en http(s), avec leurs seuls
   * attributs de lecture (`controls` forcé, jamais `autoplay`) et leur mise en
   * page. `<source>` reste retiré. Désactivé par défaut.
   */
  allowVideos?: boolean;
}) {
  const withMedia = allowImages || allowYoutubeEmbeds || allowVideos;
  const purifier = withMedia ? getMediaPurifier() : DOMPurify;

  const clean = purifier.sanitize(html ?? '', {
    ALLOWED_TAGS: [
      ...ALLOWED_TAGS,
      ...(allowImages ? ['img'] : []),
      ...(allowYoutubeEmbeds ? ['iframe'] : []),
      ...(allowVideos ? ['video'] : []),
    ],
    ALLOWED_ATTR: [
      ...ALLOWED_ATTR,
      ...(allowImages ? IMAGE_ATTR : []),
      ...(allowYoutubeEmbeds ? IFRAME_ATTR : []),
      ...(allowVideos ? VIDEO_ATTR : []),
    ],
    // Force les liens externes à s'ouvrir proprement sans risque tabnabbing.
    ADD_ATTR: ['target'],
  });
  return <div className={className} dangerouslySetInnerHTML={{ __html: clean }} />;
}
